import { Box, createStyles, Group, Loader, lighten, Stack, TFText } from '@tf/ui';
import type React from 'react';

import type { DocumentCheck } from '../../../../../../api/documentCheck';

import { Result } from './Result';

const useStyles = createStyles(({ colors, radius }) => ({
	infoBlockWrapper: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '.75rem .75rem',
		borderRadius: radius.sm,
		backgroundColor: colors.gray[1],
	},
	infoBlockContent: {
		display: 'block',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginRight: '.5rem',
		cursor: 'default',
		color: colors.brand[6],
	},
	errorBlockWrapper: {
		backgroundColor: lighten(colors.red[1], 0.6),
	},
}));

type JobError = {
	documentation?: string;
	identifier?: string;
	message?: string;
	severity?: string;
};

const getErrorMessage = (check: DocumentCheck) => {
	const defaultMessage = 'Please try again later or contact support.';

	if (
		check.data.response &&
		'details' in check.data.response &&
		typeof check.data.response.details === 'object' &&
		check.data.response.details !== null &&
		'error' in check.data.response.details
	) {
		try {
			const error: JobError = JSON.parse(check.data.response.details.error as string);
			return error?.message ?? defaultMessage;
		} catch (e) {
			console.error('Error parsing job result details:', e);
			return defaultMessage;
		}
	}
};

interface Props {
	check: DocumentCheck;
	isReadOnly: boolean;
}

export const DocumentsCheckStatus: React.FC<Props> = ({ check, isReadOnly }) => {
	const { classes } = useStyles();

	return (
		<Group
			gap="sm"
			sx={({ radius, colors }) => ({
				display: 'flex',
				alignItems: 'center',
				borderRadius: radius.sm,
				backgroundColor: colors.gray[0],
			})}
		>
			{check.meta.state === 'NEW' || check.meta.state === 'LIMITED' ? (
				<Box className={classes.infoBlockWrapper}>
					<Group>
						<Loader size={18} />
						<TFText size="sm" lineClamp={2} className={classes.infoBlockContent}>
							The document is being checked. This operation can take up to 20 minutes depending on the document quality.
						</TFText>
					</Group>
				</Box>
			) : null}

			{check.meta.state === 'FAILED' ? (
				<Box className={`${classes.infoBlockWrapper} ${!isReadOnly && classes.errorBlockWrapper}`}>
					<Stack gap="xs">
						<TFText size="sm" lineClamp={1}>
							Could not check the document.
						</TFText>

						{!isReadOnly ? (
							<TFText size="sm" lineClamp={3}>
								{getErrorMessage(check)}
							</TFText>
						) : null}
					</Stack>
				</Box>
			) : null}

			{check.meta.state === 'COMPLETED' && <Result check={check} />}
		</Group>
	);
};
